import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateLeagueInput, CreateInviteInput } from '@pandacalcio/validation';
import { api } from '../../lib/api';

// ------------------------------------------------------------
// Tipi risposta API
// ------------------------------------------------------------

export interface LeagueSummary {
  membership: { id: string; role: 'ADMIN' | 'PLAYER'; status: string };
  league: {
    id: string;
    name: string;
    slug: string;
    logoUrl: string | null;
    status: string;
    competition: string;
    seasonLabel: string;
    memberCount: number;
    maxParticipants: number;
  };
}

export interface LeagueDetail {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  status: string;
  timezone: string;
  maxParticipants: number;
  memberCount: number;
  competition: string;
  seasonLabel: string;
  startDate: string | null;
  endDate: string | null;
  ruleset: { name: string; version: number } | null;
  myRole: 'ADMIN' | 'PLAYER' | 'SUPER_ADMIN' | null;
  automation: {
    autoDeadline: boolean;
    autoDeadlineMinutesBefore: number;
    autoScoring: boolean;
    autoCreate: boolean;
  };
}

export interface LeagueMemberItem {
  id: string;
  role: 'ADMIN' | 'PLAYER';
  status: string;
  isBot: boolean;
  displayName: string;
  joinedAt: string;
}

export interface InviteItem {
  id: string;
  code: string;
  email: string | null;
  maxUses: number;
  usedCount: number;
  requiresApproval: boolean;
  expiresAt: string | null;
  status: string;
  createdAt: string;
}

export interface SeasonOption {
  id: string;
  label: string;
}

export interface JoinInfo {
  valid: boolean;
  league: {
    name: string;
    logoUrl: string | null;
    competition: string;
    seasonLabel: string;
    memberCount: number;
    maxParticipants: number;
  };
  requiresApproval: boolean;
  admissionRequirements: string[];
  quizRequired: boolean;
}

// ------------------------------------------------------------
// Query hooks
// ------------------------------------------------------------

export function useMyLeagues() {
  return useQuery({
    queryKey: ['leagues'],
    queryFn: () => api<{ leagues: LeagueSummary[] }>('/leagues').then((r) => r.leagues),
  });
}

export function useLeague(id: string) {
  return useQuery({
    queryKey: ['leagues', id],
    queryFn: () => api<{ league: LeagueDetail }>(`/leagues/${id}`).then((r) => r.league),
    enabled: !!id,
  });
}

export function useLeagueMembers(id: string) {
  return useQuery({
    queryKey: ['leagues', id, 'members'],
    queryFn: () => api<{ members: LeagueMemberItem[] }>(`/leagues/${id}/members`).then((r) => r.members),
    enabled: !!id,
  });
}

/**
 * Invito permanente della lega: il server lo riusa o lo crea, così l'admin
 * ha sempre un link da condividere.
 */
export function usePermanentInvite(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['leagues', id, 'permanent-invite'],
    queryFn: () =>
      api<{ invite: InviteItem }>(`/leagues/${id}/invites/permanent`, {
        method: 'POST',
        body: {},
      }).then((r) => r.invite),
    enabled: !!id && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useLeagueInvites(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['leagues', id, 'invites'],
    queryFn: () => api<{ invites: InviteItem[] }>(`/leagues/${id}/invites`).then((r) => r.invites),
    enabled: !!id && enabled,
  });
}

export function useSeasons() {
  return useQuery({
    queryKey: ['seasons'],
    queryFn: () => api<{ seasons: SeasonOption[] }>('/seasons').then((r) => r.seasons),
  });
}

export function useJoinInfo(code: string) {
  return useQuery({
    queryKey: ['join', code],
    queryFn: () => api<JoinInfo>(`/join/${code}`),
    enabled: !!code,
    retry: false,
  });
}

// ------------------------------------------------------------
// Mutation hooks
// ------------------------------------------------------------

export function useCreateLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeagueInput) =>
      api<{ league: { id: string; name: string } }>('/leagues', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues'] }),
  });
}

export function useCreateInvite(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInviteInput) =>
      api<{ invite: InviteItem }>(`/leagues/${leagueId}/invites`, { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'invites'] }),
  });
}

export function useRevokeInvite(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) =>
      api(`/leagues/${leagueId}/invites/${inviteId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'invites'] }),
  });
}

/** Aggiorna un membro della lega (es. rinominare il giocatore bot). */
export function useUpdateMember(leagueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { memberId: string; botName?: string }) =>
      api(`/leagues/${leagueId}/members/${input.memberId}`, {
        method: 'PATCH',
        body: { ...(input.botName !== undefined ? { botName: input.botName } : {}) },
      }),
    onSuccess: () => {
      // Il nome del bot compare in membri, classifiche e tabelloni: aggiorna tutto.
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId] });
    },
  });
}

export function useJoinLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api<{ leagueId: string; pending: boolean; quizRequired: boolean; message: string }>(
        `/join/${code}`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leagues'] }),
  });
}
